/**
 * useKtsPhasedSession — React hook owning the phased session lifecycle.
 *
 * Components consume this hook to drive Run → Prework → Action → Stop →
 * wind-down. The hook holds: current session state, last error, an event
 * buffer (frontend side, flushed via batched POSTs), and exposes phase
 * transition methods.
 *
 * Backend contract: see docs/KTS_PHASED_SESSION_API.md
 * REST client: src/lib/kts-v1-client.ts
 *
 * Usage:
 *
 *   const {
 *     status,
 *     session,
 *     error,
 *     start,        // POST /sessions (creates + tollbooth)
 *     submitPrework,// POST /:id/prework
 *     beginAction,  // POST /:id/action/start
 *     emitEvent,    // adds event to local buffer; flushes on schedule
 *     finishAction, // flush buffer + POST /:id/action/stop
 *     finalize,     // POST /:id/finish; polls until complete
 *     abandon,      // POST /:id/abandon
 *   } = useKtsPhasedSession({ toolId: 'kinetic.pushup.amrap.v1', authToken });
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  abandonSession,
  type CanonicalActionEvent,
  type CreateSessionInput,
  createKineticSession,
  finishSession,
  getSession,
  joinSession as joinSessionApi,
  type KineticSession,
  KtsV1Error,
  type PartnerRole,
  type PreworkInput,
  runPrework,
  type SessionMode,
  type SessionStatus,
  startAction,
  stopAction,
  submitActionEvents,
} from './kts-v1-client';

// ───────────────────────────────────────────────────────────────────────────
// Hook input + output
// ───────────────────────────────────────────────────────────────────────────

export interface UseKtsPhasedSessionInput {
  toolId: string;
  authToken: string;
  /** Mode picker. Defaults to undefined (server uses tool.default_mode). */
  mode?: SessionMode;
  partnerId?: string;
  partnerRole?: PartnerRole;
  /** Hybrid batch trigger (default: 10 events OR 500ms, whichever first). */
  batchSize?: number;
  batchTimeoutMs?: number;
  /** Polling interval during wind_down (default 2000ms). */
  pollIntervalMs?: number;
  /** Optional client metadata to seal into tollbooth. */
  clientMetadata?: Record<string, unknown>;
}

/** Frontend-visible state. Combines backend status with ephemeral UI bits. */
export type FrontendStatus =
  | 'idle'                  // never started; user hasn't clicked Run
  | SessionStatus
  | 'connecting'            // POST /sessions in flight before tollbooth resolves
  | 'submitting_prework'    // POST /prework in flight
  | 'starting_action'       // POST /action/start in flight
  | 'stopping_action'       // POST /action/stop in flight
  | 'finalizing'            // POST /finish in flight (transient before wind_down)
  | 'polling'               // wind_down + we are actively polling
  ;

export interface UseKtsPhasedSession {
  /** Current frontend status — superset of backend status with transitional UI states. */
  status: FrontendStatus;
  /** Latest session record from backend (null until first /sessions response). */
  session: KineticSession | null;
  /** Last error encountered, surfaced for UI. */
  error: KtsV1Error | null;
  /** Event buffer size (debug). */
  bufferedEventCount: number;

  // Phase transitions
  start: () => Promise<void>;
  joinAsPartner: () => Promise<void>;
  submitPrework: (input: PreworkInput) => Promise<void>;
  beginAction: () => Promise<void>;
  emitEvent: (event: CanonicalActionEvent) => void;
  emitEvents: (events: CanonicalActionEvent[]) => void;
  finishAction: (args?: {
    duration_s?: number;
    reason?: 'user_finished' | 'user_aborted' | 'timer_expired';
  }) => Promise<void>;
  finalize: () => Promise<void>;
  abandon: (reason?: string) => Promise<void>;
  /** Manual reset — clears session for a fresh run. */
  reset: () => void;
}

// ───────────────────────────────────────────────────────────────────────────
// Hook
// ───────────────────────────────────────────────────────────────────────────

const DEFAULT_BATCH_SIZE = 10;
const DEFAULT_BATCH_TIMEOUT_MS = 500;
const DEFAULT_POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

export function useKtsPhasedSession(input: UseKtsPhasedSessionInput): UseKtsPhasedSession {
  const {
    toolId,
    authToken,
    mode,
    partnerId,
    partnerRole,
    batchSize = DEFAULT_BATCH_SIZE,
    batchTimeoutMs = DEFAULT_BATCH_TIMEOUT_MS,
    pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
    clientMetadata,
  } = input;

  const [status, setStatus] = useState<FrontendStatus>('idle');
  const [session, setSession] = useState<KineticSession | null>(null);
  const [error, setError] = useState<KtsV1Error | null>(null);

  // Event buffer + flush timer
  const bufferRef = useRef<CanonicalActionEvent[]>([]);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionRef = useRef<KineticSession | null>(null);
  const pollAbortRef = useRef<AbortController | null>(null);

  const [bufferedEventCount, setBufferedEventCount] = useState(0);

  // Keep sessionRef in sync so async callbacks see the latest id
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      if (pollAbortRef.current) pollAbortRef.current.abort();
    };
  }, []);

  // ── Flush helper ─────────────────────────────────────────────────────────

  const flushBuffer = useCallback(async () => {
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    const sid = sessionRef.current?.session_id;
    if (!sid || bufferRef.current.length === 0) return;

    const toSend = bufferRef.current;
    bufferRef.current = [];
    setBufferedEventCount(0);

    try {
      await submitActionEvents(authToken, sid, toSend);
    } catch (e) {
      // On failure, push the events back to the front of the buffer for retry.
      // The user keeps moving; we'll retry on the next flush trigger.
      bufferRef.current = [...toSend, ...bufferRef.current];
      setBufferedEventCount(bufferRef.current.length);
      if (e instanceof KtsV1Error) setError(e);
      else console.error('[kts-phased] flush failed', e);
    }
  }, [authToken]);

  const scheduleFlush = useCallback(() => {
    if (flushTimerRef.current) return;
    flushTimerRef.current = setTimeout(() => {
      flushTimerRef.current = null;
      void flushBuffer();
    }, batchTimeoutMs);
  }, [batchTimeoutMs, flushBuffer]);

  // ── Phase methods ────────────────────────────────────────────────────────

  const start = useCallback(async () => {
    setError(null);
    setStatus('connecting');
    try {
      const createInput: CreateSessionInput = {
        tool_id: toolId,
        mode,
        partner_id: partnerId,
        partner_role: partnerRole,
        client_metadata: clientMetadata,
      };
      const res = await createKineticSession(authToken, createInput);
      setSession(res.session);
      setStatus(res.session.status ?? 'prework');
    } catch (e) {
      if (e instanceof KtsV1Error) {
        setError(e);
        if (e.session) setSession(e.session);
        setStatus(e.session?.status ?? 'failed');
      } else {
        setError(new KtsV1Error({
          code: 'unknown',
          message: (e as Error).message,
          status: 0,
        }));
        setStatus('failed');
      }
    }
  }, [authToken, toolId, mode, partnerId, partnerRole, clientMetadata]);

  const joinAsPartner = useCallback(async () => {
    if (!sessionRef.current) {
      throw new Error('no session to join — primary must call start() first and share session_id');
    }
    setError(null);
    try {
      const res = await joinSessionApi(authToken, sessionRef.current.session_id);
      setSession(res.session);
    } catch (e) {
      if (e instanceof KtsV1Error) setError(e);
    }
  }, [authToken]);

  const submitPrework = useCallback(async (preworkInput: PreworkInput) => {
    const sid = sessionRef.current?.session_id;
    if (!sid) throw new Error('no session');
    setError(null);
    setStatus('submitting_prework');
    try {
      const res = await runPrework(authToken, sid, preworkInput);
      setSession(res.session);
      setStatus(res.session.status ?? 'ready');
    } catch (e) {
      if (e instanceof KtsV1Error) {
        setError(e);
        if (e.session) setSession(e.session);
        setStatus(e.session?.status ?? 'failed');
      }
    }
  }, [authToken]);

  const beginAction = useCallback(async () => {
    const sid = sessionRef.current?.session_id;
    if (!sid) throw new Error('no session');
    setError(null);
    setStatus('starting_action');
    try {
      const res = await startAction(authToken, sid);
      setSession(res.session);
      setStatus(res.session.status ?? 'action_active');
    } catch (e) {
      if (e instanceof KtsV1Error) {
        setError(e);
        if (e.session) setSession(e.session);
        setStatus(e.session?.status ?? 'failed');
      }
    }
  }, [authToken]);

  const emitEvent = useCallback((event: CanonicalActionEvent) => {
    bufferRef.current.push(event);
    setBufferedEventCount(bufferRef.current.length);
    if (bufferRef.current.length >= batchSize) {
      void flushBuffer();
    } else {
      scheduleFlush();
    }
  }, [batchSize, flushBuffer, scheduleFlush]);

  const emitEvents = useCallback((events: CanonicalActionEvent[]) => {
    bufferRef.current.push(...events);
    setBufferedEventCount(bufferRef.current.length);
    if (bufferRef.current.length >= batchSize) {
      void flushBuffer();
    } else {
      scheduleFlush();
    }
  }, [batchSize, flushBuffer, scheduleFlush]);

  const finishAction = useCallback(async (args?: {
    duration_s?: number;
    reason?: 'user_finished' | 'user_aborted' | 'timer_expired';
  }) => {
    const sid = sessionRef.current?.session_id;
    if (!sid) throw new Error('no session');
    setError(null);
    setStatus('stopping_action');

    // Flush any pending events before stop, so the action slot has them all.
    await flushBuffer();

    try {
      const res = await stopAction(authToken, sid, {
        duration_s: args?.duration_s,
        reason: args?.reason ?? 'user_finished',
      });
      setSession(res.session);
      setStatus(res.session.status ?? 'action_closing');
    } catch (e) {
      if (e instanceof KtsV1Error) {
        setError(e);
        if (e.session) setSession(e.session);
        setStatus(e.session?.status ?? 'failed');
      }
    }
  }, [authToken, flushBuffer]);

  // ── Polling helper for wind_down → complete ──────────────────────────────

  const startPolling = useCallback(async () => {
    const sid = sessionRef.current?.session_id;
    if (!sid) return;

    if (pollAbortRef.current) pollAbortRef.current.abort();
    const ctrl = new AbortController();
    pollAbortRef.current = ctrl;

    const startTime = Date.now();
    setStatus('polling');

    while (!ctrl.signal.aborted) {
      if (Date.now() - startTime > POLL_TIMEOUT_MS) {
        setError(new KtsV1Error({
          code: 'poll_timeout',
          message: `Wind-down didn't complete within ${POLL_TIMEOUT_MS / 1000}s`,
          status: 0,
        }));
        setStatus('failed');
        return;
      }

      try {
        const res = await getSession(authToken, sid);
        setSession(res.session);
        const s = res.session.status;
        if (s === 'complete' || s === 'failed' || s === 'abandoned') {
          setStatus(s);
          return;
        }
      } catch (e) {
        if (e instanceof KtsV1Error) {
          setError(e);
          // Keep polling on transient errors; only bail on auth failures
          if (e.status === 401 || e.status === 403) {
            setStatus('failed');
            return;
          }
        }
      }

      await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
    }
  }, [authToken, pollIntervalMs]);

  const finalize = useCallback(async () => {
    const sid = sessionRef.current?.session_id;
    if (!sid) throw new Error('no session');
    console.log('[useKtsPhasedSession] finalize() → POST /finish', sid);
    setError(null);
    setStatus('finalizing');
    try {
      const res = await finishSession(authToken, sid);
      console.log('[useKtsPhasedSession] /finish OK, status:', res.session.status);
      setSession(res.session);
      setStatus(res.session.status ?? 'wind_down');
      // Kick polling immediately
      void startPolling();
    } catch (e) {
      console.error('[useKtsPhasedSession] /finish FAILED', e);
      if (e instanceof KtsV1Error) {
        setError(e);
        if (e.session) setSession(e.session);
        setStatus(e.session?.status ?? 'failed');
      }
    }
  }, [authToken, startPolling]);

  const abandon = useCallback(async (reason?: string) => {
    const sid = sessionRef.current?.session_id;
    if (!sid) {
      // Nothing to abandon — just reset
      setSession(null);
      setStatus('idle');
      return;
    }
    if (pollAbortRef.current) pollAbortRef.current.abort();
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    bufferRef.current = [];
    setBufferedEventCount(0);

    try {
      const res = await abandonSession(authToken, sid, reason ?? 'user_cancelled');
      setSession(res.session);
      setStatus(res.session.status ?? 'abandoned');
    } catch (e) {
      if (e instanceof KtsV1Error) setError(e);
    }
  }, [authToken]);

  const reset = useCallback(() => {
    if (pollAbortRef.current) pollAbortRef.current.abort();
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    bufferRef.current = [];
    setBufferedEventCount(0);
    setSession(null);
    setError(null);
    setStatus('idle');
  }, []);

  return {
    status,
    session,
    error,
    bufferedEventCount,
    start,
    joinAsPartner,
    submitPrework,
    beginAction,
    emitEvent,
    emitEvents,
    finishAction,
    finalize,
    abandon,
    reset,
  };
}
